import type { UnitsSettings } from "../../../app/units/units";
import { UndoOutlined } from "@ant-design/icons";
import type { ListActionRef, ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { App, Button, Space, Tag, Typography } from "../../../../../../../shared/frontend/ui";
import React from "react";
import {
  PERSON_CHANGE_KIND_APP_CONSENT,
  PERSON_CHANGE_KIND_ARTICLE_SALE_CANCELLED,
  PERSON_CHANGE_KIND_ARTICLE_SALE_CREATED,
  PERSON_CHANGE_KIND_ARTICLE_SALE_DELETED,
  PERSON_CHANGE_KIND_ARTICLE_SALE_SEPA_GROUP_CHANGED,
  PERSON_CHANGE_KIND_CHARGEBACK_FOLLOW_UP_COMPLETED,
  PERSON_CHANGE_KIND_CONTRACT_PACKAGE_CANCELLED,
  PERSON_CHANGE_KIND_CONTRACT_PACKAGE_CREATED,
  PERSON_CHANGE_KIND_MEMBER_APP_REQUEST,
  PERSON_CHANGE_KIND_PERSON_DATA_CHANGE,
  PERSON_CHANGE_KIND_PERSON_DATA_CHANGE_EVENT,
  PERSON_CHANGE_KIND_PERSON_DATA_UNDO,
  PERSON_CHANGE_KIND_REMINDER_BLOCK_RELEASED,
  PERSON_CHANGE_KIND_REMINDER_BLOCKED,
  PERSON_CHANGE_KIND_REMINDER_LEVEL_RESET,
  PERSON_CHANGE_KIND_REMINDER_RETRY_CREATED,
  PERSON_CHANGE_KIND_REMINDER_RETRY_NOTIFICATION_CONFIRMED,
  PERSON_CHANGE_KIND_WALLET_CREATED
} from "../../../app/constants/personChangeHistoryKind";
import { Permissions } from "../../../app/constants/permissions";
import { spacing } from "../../../app/design-system";
import { formatDateTimeIso } from "../../../app/units/format";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import { SettingsListPage } from "../../../components/SettingsListPage";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";

const CHANGE_KIND_CONTRIBUTION_CANCELLED = "ContributionCancelled";
const HISTORY_STATUS_UNDONE = "undone";
const HISTORY_STATUS_UNDO_EVENT = "undoEvent";
const HISTORY_STATUS_BLOCKED = "blocked";
/** Länge von `YYYY-MM-DDTHH:mm:ss`. */
const ISO_DATE_TIME_SECONDS_LENGTH = 19;
const OCCURRED_AT_COLUMN_WIDTH = 160;
const AREA_COLUMN_WIDTH = 140;
const TITLE_COLUMN_WIDTH = 180;
const STATUS_COLUMN_WIDTH = 130;
const ACTIONS_COLUMN_WIDTH = 170;

type PersonChangeHistoryRow = {
  id: string;
  kind: string;
  occurredAt: string;
  description: string;
  title?: string | null;
  source?: string | null;
  affectedObjectId?: string | null;
  undoable?: boolean;
  undoBlockedReason?: string | null;
  undoPreview?: string | null;
  status?: string | null;
  area?: string | null;
};

type PersonChangeHistoryTabProps = {
  auth: {
    api: {
      get<T>(url: string): Promise<T>;
      post<T>(url: string, body?: unknown): Promise<T>;
    };
    hasAnyPermission(...permissions: string[]): boolean;
  };
  personId: string;
  text: (key: string) => string;
  units: UnitsSettings;
};

const eventIdFromRow = (row: PersonChangeHistoryRow) => row.id.startsWith("event-") ? row.id.slice("event-".length) : "";

export function PersonChangeHistoryTab({ auth, personId, text, units }: PersonChangeHistoryTabProps) {
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const actionRef = React.useRef<ListActionRef | null>(null);
  const canUndo = auth.hasAnyPermission(Permissions.PersonsChangeHistoryUndo);

  const kindLabel = (k: string) => {
    if (k === CHANGE_KIND_CONTRIBUTION_CANCELLED) return text("openItems.cancel.history");
    if (k === PERSON_CHANGE_KIND_PERSON_DATA_CHANGE) return text("Person data");
    if (k === PERSON_CHANGE_KIND_PERSON_DATA_CHANGE_EVENT) return text("Person data change");
    if (k === PERSON_CHANGE_KIND_PERSON_DATA_UNDO) return text("Undo");
    if (k === PERSON_CHANGE_KIND_APP_CONSENT) return text("Consent (App)");
    if (k === PERSON_CHANGE_KIND_MEMBER_APP_REQUEST) return text("Cancellation / Rest period");
    if (k === PERSON_CHANGE_KIND_CONTRACT_PACKAGE_CREATED) return text("Contract created");
    if (k === PERSON_CHANGE_KIND_CONTRACT_PACKAGE_CANCELLED) return text("Contract cancelled");
    if (k === PERSON_CHANGE_KIND_WALLET_CREATED) return text("Wallet created");
    if (k === PERSON_CHANGE_KIND_REMINDER_LEVEL_RESET) return text("ReminderLevelReset");
    if (k === PERSON_CHANGE_KIND_REMINDER_RETRY_CREATED) return text("ReminderRetryCreated");
    if (k === PERSON_CHANGE_KIND_REMINDER_RETRY_NOTIFICATION_CONFIRMED) return text("ReminderRetryNotificationConfirmed");
    if (k === PERSON_CHANGE_KIND_REMINDER_BLOCKED) return text("Reminder block set");
    if (k === PERSON_CHANGE_KIND_REMINDER_BLOCK_RELEASED) return text("Reminder block released");
    if (k === PERSON_CHANGE_KIND_ARTICLE_SALE_CREATED) return text("Article sale created");
    if (k === PERSON_CHANGE_KIND_ARTICLE_SALE_CANCELLED) return text("Article sale cancelled");
    if (k === PERSON_CHANGE_KIND_ARTICLE_SALE_DELETED) return text("Article sale deleted");
    if (k === PERSON_CHANGE_KIND_ARTICLE_SALE_SEPA_GROUP_CHANGED) return text("Article sale sepa group changed");
    if (k === PERSON_CHANGE_KIND_CHARGEBACK_FOLLOW_UP_COMPLETED) return text("Chargeback task done message");
    return k;
  };

  const statusLabel = (status?: string | null) => {
    if (status === HISTORY_STATUS_UNDONE) return text("rolled back");
    if (status === HISTORY_STATUS_UNDO_EVENT) return text("rollback");
    if (status === HISTORY_STATUS_BLOCKED) return text("blocked");
    return text("recorded");
  };

  const statusColor = (status?: string | null) => {
    if (status === HISTORY_STATUS_UNDONE || status === HISTORY_STATUS_UNDO_EVENT) return "purple";
    if (status === HISTORY_STATUS_BLOCKED) return "orange";
    return "blue";
  };

  const undo = React.useCallback((row: PersonChangeHistoryRow) => {
    const eventId = eventIdFromRow(row);
    if (!eventId) return;
    modal.confirm({
      title: text("Undo change?"),
      content: (
        <Space direction="vertical" size={spacing.sm}>
          <Typography.Text>{row.undoPreview || text("The selected change will be rolled back.")}</Typography.Text>
          <Typography.Text type="secondary">{row.description}</Typography.Text>
        </Space>
      ),
      okText: text("Undo"),
      cancelText: text("Cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        await auth.api.post(`/api/v1/persons/${encodeURIComponent(personId)}/change-history/${encodeURIComponent(eventId)}/undo`, {});
        feedback.success(text("Change rolled back."));
        actionRef.current?.reload?.();
      }
    });
  }, [auth.api, feedback, modal, personId, text]);

  const changeHistoryColumnDefinitions: ColumnDefinition[] = [
    { key: "occurredAt", label: text("Date"), defaultVisible: true },
    { key: "area", label: text("Area"), defaultVisible: true },
    { key: "title", label: text("Action"), defaultVisible: true },
    { key: "status", label: text("Status"), defaultVisible: true },
    { key: "description", label: text("Description"), defaultVisible: true }
  ];

  const changeHistoryAllColumnsMap: Record<string, ListColumn<PersonChangeHistoryRow>> = {
    occurredAt: {
      title: text("Date"),
      dataIndex: "occurredAt",
      key: "occurredAt",
      width: OCCURRED_AT_COLUMN_WIDTH,
      sorter: (a, b) => String(a.occurredAt || "").localeCompare(String(b.occurredAt || "")),
      render: (_: unknown, r: PersonChangeHistoryRow) =>
        r.occurredAt
          ? formatDateTimeIso(String(r.occurredAt).slice(0, ISO_DATE_TIME_SECONDS_LENGTH), units, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
          : "-"
    },
    area: {
      title: text("Area"),
      dataIndex: "area",
      key: "area",
      width: AREA_COLUMN_WIDTH,
      sorter: (a, b) => String(a.area || "").localeCompare(String(b.area || "")),
      render: (_: unknown, r: PersonChangeHistoryRow) => r.area || kindLabel(r.kind)
    },
    title: {
      title: text("Action"),
      dataIndex: "title",
      key: "title",
      width: TITLE_COLUMN_WIDTH,
      sorter: (a, b) => String(a.title || "").localeCompare(String(b.title || "")),
      render: (_: unknown, r: PersonChangeHistoryRow) => r.title || kindLabel(r.kind)
    },
    status: {
      title: text("Status"),
      dataIndex: "status",
      key: "status",
      width: STATUS_COLUMN_WIDTH,
      render: (_: unknown, r: PersonChangeHistoryRow) => <Tag color={statusColor(r.status)}>{statusLabel(r.status)}</Tag>
    },
    description: {
      title: text("Description"),
      dataIndex: "description",
      key: "description",
      ellipsis: false,
      render: (_: unknown, r: PersonChangeHistoryRow) => r.description || "-"
    }
  };

  const actionsColumn: ListColumn<PersonChangeHistoryRow> & { slotCount?: number } = {
    title: "",
    key: "actions",
    valueType: "option",
    width: ACTIONS_COLUMN_WIDTH,
    slotCount: 1,
    render: (_: unknown, row: PersonChangeHistoryRow) => {
      if (!row.undoable || !canUndo) return null;
      const button = (
        <Button
          size="small"
          type="link"
          danger
          icon={<UndoOutlined />}
          onClick={() => undo(row)}
        >
          {text("Undo")}
        </Button>
      );
      return button;
    }
  };

  const requestHistory = React.useCallback(async () => {
    const list = (await auth.api.get<PersonChangeHistoryRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/change-history`)) || [];
    return { data: list, success: true, total: list.length };
  }, [auth.api, personId]);

  return (
    <SettingsListPage<PersonChangeHistoryRow>
      storageKey="persons.detail.changeHistory"
      rowKey="id"
      request={requestHistory}
      columnDefinitions={changeHistoryColumnDefinitions}
      allColumnsMap={changeHistoryAllColumnsMap}
      actionsColumn={actionsColumn}
      pagination={{ pageSize: 25 }}
      settingsOnlyColumnWidth={36}
      actionRef={actionRef}
    />
  );
}
